import React from 'react';
import immutable from 'immutable';
import ArrowDown from 'material-ui/svg-icons/navigation/arrow-drop-down';
import IconButton from 'material-ui/IconButton';
import IconMenu from 'material-ui/IconMenu';
import Theme from 'styles/theme';
import classNames from 'classnames';
import SearchInputActions from 'actions/search/SearchInputActions';
import StoreUtils from 'stores/Utils';
import SearchTypeToolTip from 'components/pure/search-type/tooltip/SearchTypeToolTip';
import isPhoneNumber from 'utils/phone/isPhoneNumber';
import {SEARCH_TYPE_MENU_ITEMS} from './const';
import 'styles/calls/SearchTypeIconMenu.styl';
import hasFeature from 'utils/hasFeature';
import ConstValues from 'utils/const';
import CallActions from 'actions/CallActions';
import SearchFieldsActions from 'actions/search/SearchFieldsActions';
import MenuItem from '@material-ui/core/MenuItem';

/**
 * <SearchTypeIconMenu /> is a dropdown that renders a list of different menu items that
 * are used to specify which search field should the current <SearchInputBox /> based
 * search be performed on
 */
export default class SearchTypeIconMenu extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      searchTypeMenuOpen: false,
      selectedMenuItem: ConstValues.searchTypes.miAll,
      menuItemsToRender: immutable.List(),
      phoneTooltipDismissed: false,
    };
    this.storeUtils = new StoreUtils();
  }

  componentDidMount() {
    this.updateStateMenuItemsToRender(this.props);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.state.phoneTooltipDismissed && nextProps.searchTerm === '') {
      this.setState({phoneTooltipDismissed: false});
    }

    if (nextProps.searchType !== ConstValues.searchTypes.miAll && nextProps.menuItemsStatus) {
      const menuItemsStatus = nextProps.menuItemsStatus;
      const status = menuItemsStatus.get(nextProps.searchType);
      if (
        status &&
        status.removed &&
        (this.props.searchTerm?.length > 0 || nextProps.searchTerm?.length > 0)
      ) {
        this.props.clearSearchTerm();
        SearchFieldsActions.clearMenuItemStatus(nextProps.searchType);
      }
    }
    this.updateStateMenuItemsToRender(nextProps);
  }

  /**
   * Return `true` if `searchFields` should be used to filter the menu items
   * @param searchFields {immutable.Map} Current search fields
   */
  shouldFilterMenuItems(searchFields) {
    searchFields = searchFields.filterNot(field => {
      return (
        field.name === 'Caller' || field.name === 'Is In Library' || field.name === 'Call Stage'
      );
    });
    return searchFields.count() > 0;
  }

  /**
   * Update `this.state.menuItemsToRender` based on `nextProps.searchTerm` and `nextProps.isSearchingSelf`. We filter
   * `this.state.menuItemsToRender` when:
   * 1) No search term is currently provided
   * OR
   * 2) User is on "My Calls" page
   * @param nextProps {object} New properties passed into this component
   */
  updateStateMenuItemsToRender(nextProps) {
    let menuItemsToRender = SEARCH_TYPE_MENU_ITEMS.filter(mi =>
      mi.flag ? hasFeature(nextProps.flags, mi.flag) : true
    );
    if (nextProps.searchFields || this.props.searchFields) {
      const searchFields = nextProps.searchFields || this.props.searchFields;
      if (this.shouldFilterMenuItems(searchFields)) {
        menuItemsToRender = this.menuItemsToRenderFilterByFields(searchFields, menuItemsToRender);
      }
    }
    if (this.props.isSearchingSelf || nextProps.isSearchingSelf) {
      menuItemsToRender = menuItemsToRender.filterNot(item => {
        return item.primaryText === ConstValues.searchTypes.miCaller;
      });
    }
    if (!menuItemsToRender.equals(this.state.menuItemsToRender)) {
      this.setState({menuItemsToRender: menuItemsToRender});
    }
  }

  /**
   * Return a list of menu items some of which are filtered out using `searchFields`
   * @param searchFields {Iterable<K, V>} Current fields that are available for search
   * @param filteredItems {Iterable<K, V>} List of menu items
   * @returns {Iterable<K, V>} List of menu items
   */
  menuItemsToRenderFilterByFields(searchFields, filteredItems) {
    const st = ConstValues.searchTypes;

    /**
     * Return `true` if `item` should be rendered no matter what
     * @param item {object} Menu item
     */
    const shouldMenuItemAlwaysBeRendered = item => {
      return immutable.List([st.miAll, st.miKeywords, st.miAnnotations]).includes(item.primaryText);
    };

    return filteredItems.filter(item => {
      const found = searchFields.find(field => {
        const miFieldPrimaryText = this.storeUtils.menuItemPrimaryTextFromSearchField(field);
        return miFieldPrimaryText === item.primaryText;
      });
      return (
        found ||
        shouldMenuItemAlwaysBeRendered(item) ||
        shouldMenuItemPhoneBeRendered(item, searchFields)
      );
    });
  }

  /** Display a overflow "dropdown" of different search types */
  onTouchTapSearchTypeDropdown = () => {
    const searchTypeMenuOpen = !this.state.searchTypeMenuOpen;
    this.setState({
      searchTypeMenuOpen: searchTypeMenuOpen,
    });
  };

  /** Render the icon of the search type according to what's selected at the moment */
  renderSearchTypeIcon() {
    const Icon = SEARCH_TYPE_MENU_ITEMS.find(item => {
      return item.primaryText === this.props.searchType;
    }).icon;
    return <Icon />;
  }

  /** Update `this.state.searchTypeMenuOpen` according to the current `open` status of <IconMenu /> */
  onRequestChangeSearchTypeDropdown = (open, reason) => {
    if (this.state.searchTypeMenuOpen !== open) {
      this.setState({searchTypeMenuOpen: open});
    }
  };

  renderMenuItems() {
    let menuItemsToRender = this.state.menuItemsToRender;
    return menuItemsToRender.map(item => {
      const searchType = item.primaryText;
      const prevSearchType = this.props.searchType;
      return (
        <MenuItem
          onClick={() =>
            setImmediate(() => {
              this.setState({
                searchTypeMenuOpen: !this.state.searchTypeMenuOpen,
              });
              if (searchType === ConstValues.searchTypes.miAll) {
                // Clear the search term and search fields state if the user switches
                // back to the 'All' search from any other search type
                SearchInputActions.clearSearchInputValue();
                CallActions.clearFieldsSearch();
              } else if (
                searchType !== ConstValues.searchTypes.miPhone &&
                prevSearchType === ConstValues.searchTypes.miPhone
              ) {
                SearchInputActions.clearSearchInputValue.defer();
                CallActions.clearSearch.defer();
                CallActions.updateSearchType.defer([prevSearchType, searchType]);
              } else {
                CallActions.updateSearchType([prevSearchType, searchType]);
              }
            })
          }
          className="search-type-menu-item"
          key={searchType}>
          <item.icon />
          {item.primaryText}
        </MenuItem>
      );
    });
  }

  onOpenPhoneSearch = () => {
    this.setState(
      {
        searchTypeMenuOpen: false,
        selectedMenuItem: ConstValues.searchTypes.miPhone,
        phoneTooltipDismissed: true,
      },
      () => CallActions.updateSearchType([this.props.searchType, ConstValues.searchTypes.miPhone])
    );
  };

  render() {
    const isSearchingPhone = this.props.searchType === ConstValues.searchTypes.miPhone;
    const shouldShowPhoneTooltip =
      isPhoneNumber(this.props.searchTerm) && !this.state.searchTypeMenuOpen && !isSearchingPhone;
    const classnames = classNames('search-type-icon-menu', [
      isSearchingPhone && 'search-type-icon-menu-phone',
    ]);
    const arrowDownClassnames = classNames([
      'arrow-down',
      isSearchingPhone && 'arrow-down-search-phone',
    ]);

    const {phoneTooltipDismissed} = this.state;

    const isShouldShowTooltip =
      shouldShowPhoneTooltip && !phoneTooltipDismissed && !isSearchingPhone;

    return (
      <div className={classnames}>
        <IconMenu
          iconButtonElement={
            <>
              {this.renderSearchTypeIcon()}
              <IconButton
                className="search-type-dropdown-icon-menu"
                iconClassName="search-type-dropdown-icon">
                <ArrowDown color={Theme.palette.minorButtonGrey} />
              </IconButton>
              {isShouldShowTooltip && (
                <SearchTypeToolTip
                  onOpenPhoneSearch={this.onOpenPhoneSearch}
                  onDismiss={() => this.setState({phoneTooltipDismissed: true})}
                />
              )}
            </>
          }
          animated
          anchorOrigin={{horizontal: 'left', vertical: 'bottom'}}
          targetOrigin={{horizontal: 'left', vertical: 'top'}}
          className={arrowDownClassnames}
          open={this.state.searchTypeMenuOpen}
          useLayerForClickAway={true}
          onRequestChange={this.onRequestChangeSearchTypeDropdown}
          onClick={this.onTouchTapSearchTypeDropdown}>
          {/* Notice that the leftIcon(s) of the MenuItem components are <div/> instead of <img src={svg}/> */}
          {/* because we want to be able to use an icon with a different color when the mouse hovers over the */}
          {/* MenuItem component and using the `onMouseHover` ReactJS event is too messy */}
          {this.renderMenuItems()}
        </IconMenu>
      </div>
    );
  }
}

/**
 * Return `true` if `item` should be rendered when its the miPhone menu item
 * and there is at least 1 type=phone search field
 * @param item {object} Menu item
 * @param searchFields {Iterable<K, V>} Current fields that are available for search
 */
function shouldMenuItemPhoneBeRendered(item, searchFields) {
  if (item.primaryText === ConstValues.searchTypes.miPhone) {
    return searchFields.find(field => field.type === 'phone');
  }
  return false;
}
